import { LabelStyles } from '@tableau/catalog-ui-temp';
import {
  getMaxCharValidation,
} from '@tableau/widgets';
import React from 'react';
import shallowEqual from 'shallowequal';

import { ReactActionDialogBodyProps,ReactActionDialogOptions } from '~commonInfra/dialogs/ReactActionDialog';

import { Messages } from '../../codegen/Localize';
import { TextFormatterOrAreaWidget } from './common/TextFormatterOrAreaWidget';

const DESCRIPTION_DIALOG_TEST_ID: string = 'description-dialog';

const MESSAGE_HEIGHT: number = 190;
const DIALOG_WIDTH: number = 500;

const textAreaClass = LabelStyles.textAreaCssClass(DESCRIPTION_DIALOG_TEST_ID);

const textAreaStyle: React.CSSProperties = {
  height: MESSAGE_HEIGHT,
  resize: 'none'
};

const markupEditorStyle: React.CSSProperties = {

};

export type DescriptionDialogBodyProps = {
  readonly useMarkup: boolean;
  readonly maxCharacterLimit: number;
}
export type DescriptionDialogState = {
  readonly description: string;
}

function isFormDataValidForSubmission(args: {
  initialFormData: DescriptionDialogState;
  currentFormData: DescriptionDialogState;
  additionalValidation: (current: DescriptionDialogState) => boolean;
}): boolean {
  const { initialFormData, currentFormData } = args;

  const formIsDirty = !shallowEqual(initialFormData, currentFormData);
  if (!formIsDirty) {
    return false;
  }

  if (!args.additionalValidation(currentFormData)) {
    return false;
  }

  return true;
}

export type DescriptionActionDialogOptions = ReactActionDialogOptions<DescriptionDialogBodyProps, DescriptionDialogState, unknown>;
export type DescriptionDialogBodyComponentProps = ReactActionDialogBodyProps<DescriptionDialogBodyProps, DescriptionDialogState>;

export function DescriptionDialogBody(props: DescriptionDialogBodyComponentProps): React.ReactElement {
  const handleDescriptionChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => props.updateDialogState({ description: e.currentTarget.value }),
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.updateDialogState]
  );
  const {message, valid} = getMaxCharValidation(props.dialogState.description, props.maxCharacterLimit);

  return (
    <TextFormatterOrAreaWidget
      label={Messages.about()}
      onChange={handleDescriptionChange}
      id={DESCRIPTION_DIALOG_TEST_ID}
      testId={DESCRIPTION_DIALOG_TEST_ID}
      valid={valid}
      message={message}
      markup={props.dialogState.description}
      useFormatter={props.useMarkup}
      className={props.useMarkup ? null : textAreaClass}
      style={props.useMarkup ? markupEditorStyle : textAreaStyle}
    />
  );
}

function checkIsValid(current: DescriptionDialogState, maximumDescriptionLength: number): boolean {
  return current.description.length <= maximumDescriptionLength;
}

export function getDescriptionDialogOptions(args: {
  initialBodyProps: DescriptionDialogBodyProps;
  initialFormData: DescriptionDialogState;
  doAction: (formData: DescriptionDialogState) => Promise<unknown>;
}): DescriptionActionDialogOptions {
  const { initialBodyProps, initialFormData, doAction } = args;

  return {
    title: Messages.editDescription2(),
    bodyComponent: DescriptionDialogBody,
    initialDialogState: initialFormData,
    initialBodyProps: initialBodyProps,
    actionName: Messages.save(),
    action: (_, dialogState) => doAction(dialogState),
    actionDisabled: (_, dialogState) => !isFormDataValidForSubmission({
      initialFormData,
      currentFormData: dialogState,
      additionalValidation: state => checkIsValid(state, initialBodyProps.maxCharacterLimit)
    }),
    dismissDialogOnActionFailed: true,
    dialogWidth: DIALOG_WIDTH
  };
}
